import {useCallback, useEffect, useState} from 'react';

/** Detects whether a line-clamped element is actually cut off, by comparing its rendered (scroll)
 * height against its visible (client) height — a clamped box's scrollHeight only exceeds its
 * clientHeight once the content overflows past the clamp. Re-measures on window resize, since a
 * clamped element's overflow depends on the column width, not just its own content. Returns a
 * callback ref (so remeasuring on mount doesn't depend on effect timing) and the current
 * truncated state. */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(): [(node: T | null) => void, boolean] {
	const [element, setElement] = useState<T | null>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const measure = useCallback(() => {
		setIsTruncated((element?.scrollHeight ?? 0) > (element?.clientHeight ?? 0));
	}, [element]);

	useEffect(() => {
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [measure]);

	useEffect(() => {
		measure();
	}, [measure, element?.clientHeight, element?.scrollHeight]);

	return [setElement, isTruncated];
}
